// reducers/applicationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notifyApiError, notifyApiSuccess } from "../../helpers/apiNotify";
import axios from "axios";
import _ from "lodash";
import qs from "qs";
import { createNotification } from "./notificationSlice";
import {
  calculateApplicationCounts,
  DISCREPANCY_STATES,
} from "../../utility/applicationCounts";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => Promise.resolve(response),
  (error) => {
    if (!error?.config?.skipErrorToast) {
      notifyApiError(error, "Request failed");
    }
    return Promise.reject(error);
  }
);

const getErrorPayload = (error, fallback) => {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;
  return {
    message,
    status: error?.response?.status,
    accessDenied:
      error?.response?.status === 403 ||
      /access denied/i.test(String(message)),
  };
};

export const isThunkFulfilled = (action) =>
  action?.meta?.requestStatus === "fulfilled";

const authConfig = (accessToken, { silent = false } = {}) => ({
  headers: {
    "ngrok-skip-browser-warning": "69420",
    "Content-Type": "application/json",
    Authorization: `${accessToken}`,
  },
  skipErrorToast: Boolean(silent),
});

const initialState = {
  fetchLoading: false,
  fetchError: null,
  message: null,
  data: [],
  allApplications: [], // Store all applications for filtering/counting purposes
  currentApp: {},
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  counts: {
    newApplications: 0,
    pendingFromDW: 0,
    discrepencies: 0,
    approved: 0,
    renewal: 0,
  },
  countsLoading: false,
  countsError: null,
  fallbackAttempted: false,
  auditTrail: {
    logs: [],
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    },
  },
};

/**
 * Save application form/data only. Does NOT send state_id — status stays unchanged
 * so the user retains permission to save a PDF afterwards.
 */
export const saveApplicationData = createAsyncThunk(
  "saveApplicationData",
  async (data, { rejectWithValue }) => {
    try {
      const payload = {};
      if (data?.json_data !== undefined) payload.json_data = data.json_data;
      if (data?.dueDate) payload.dueDate = data.dueDate;
      if (data?.descripencie != null) payload.descripencie = data.descripencie;
      if (data?.remarks != null && data.remarks !== "") {
        payload.remarks = data.remarks;
      }
      if (data?.htmlForm) payload.htmlForm = data.htmlForm;

      const response = await API.put(
        `/applications/${data?.id}`,
        payload,
        authConfig(data.accessToken, { silent: data?.silent })
      );

      if (!data?.silent) {
        notifyApiSuccess("Application data saved successfully!");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Failed to save application")
      );
    }
  }
);

/**
 * Update application status only (after data/PDF are saved when those steps apply).
 */
export const updateApplicationStatus = createAsyncThunk(
  "updateApplicationStatus",
  async (data, { rejectWithValue }) => {
    try {
      const payload = {};
      if (data?.state != null && data.state !== "") {
        payload.state_id = data.state;
      }
      if (data?.dueDate) payload.dueDate = data.dueDate;
      if (data?.remarks != null && data.remarks !== "") {
        payload.remarks = data.remarks;
      }
      if (data?.descripencie != null) payload.descripencie = data.descripencie;

      const response = await API.put(
        `/applications/${data?.id}`,
        payload,
        authConfig(data.accessToken, { silent: data?.silent })
      );

      if (!data?.silent) {
        notifyApiSuccess(
          data?.successMessage || "Your application has been updated!"
        );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Failed to update application status")
      );
    }
  }
);

/**
 * Create/save application PDF while status still allows it.
 */
export const safePdf = createAsyncThunk(
  "safePdf",
  async (data, { rejectWithValue }) => {
    const config = authConfig(data.accessToken, { silent: true });
    const payload = {};
    if (data?.htmlForm) payload.htmlForm = data.htmlForm;
    if (data?.user) payload.user = data.user;

    const saveHtmlViaPut = async () => {
      const response = await API.put(
        `/applications/${data?.id}`,
        { htmlForm: data.htmlForm },
        config
      );
      if (!data?.silent) {
        notifyApiSuccess("Your PDF has been saved!");
      }
      return response.data;
    };

    if (data?.preferPut && data?.htmlForm) {
      try {
        return await saveHtmlViaPut();
      } catch (error) {
        return rejectWithValue(getErrorPayload(error, "Failed to save PDF"));
      }
    }

    try {
      const response = await API.post(
        `/applications/${data?.id}/savepdf`,
        payload,
        config
      );
      if (!data?.silent) {
        notifyApiSuccess("Your PDF has been saved!");
      }
      return response.data;
    } catch (error) {
      const errPayload = getErrorPayload(error, "Failed to save PDF");
      if (errPayload.accessDenied && data?.htmlForm) {
        try {
          return await saveHtmlViaPut();
        } catch (fallbackError) {
          return rejectWithValue(
            getErrorPayload(fallbackError, "Failed to save PDF")
          );
        }
      }
      return rejectWithValue(errPayload);
    }
  }
);

/**
 * Combined update (legacy). Prefer saveApplicationData / updateApplicationStatus /
 * saveDataPdfThenStatus so status is never changed before PDF save.
 * When both form data and a new status are provided, saves data first (no status),
 * then updates status — never in one request that would lock PDF permissions early.
 */
export const updateApplication = createAsyncThunk(
  "updateApplication",
  async (data, { dispatch, getState, rejectWithValue }) => {
    try {
      const hasStatus = data?.state != null && data.state !== "";
      // null descripencie must NOT count as form data (callers often pass null)
      const hasFormData =
        data?.json_data !== undefined ||
        (data?.descripencie != null) ||
        data?.htmlForm;

      // Split save-data vs status when both are present (PDF-safe order)
      if (hasStatus && hasFormData) {
        const saveRes = await dispatch(
          saveApplicationData({
            id: data.id,
            accessToken: data.accessToken,
            json_data: data.json_data,
            dueDate: data.dueDate,
            descripencie: data.descripencie,
            remarks: data.remarks,
            htmlForm: data.htmlForm,
            silent: true,
          })
        );
        if (!isThunkFulfilled(saveRes)) {
          if (!data?.silent) {
            notifyApiError(saveRes.payload, "Failed to save application");
          }
          return rejectWithValue(
            saveRes.payload ||
              getErrorPayload(null, "Failed to save application")
          );
        }

        if (data?.htmlForm) {
          const pdfRes = await dispatch(
            safePdf({
              id: data.id,
              accessToken: data.accessToken,
              htmlForm: data.htmlForm,
              silent: true,
            })
          );
          if (!isThunkFulfilled(pdfRes)) {
            if (!data?.silent) {
              notifyApiError(pdfRes.payload, "Failed to save PDF");
            }
            return rejectWithValue(
              pdfRes.payload || getErrorPayload(null, "Failed to save PDF")
            );
          }
        }

        const statusRes = await dispatch(
          updateApplicationStatus({
            id: data.id,
            accessToken: data.accessToken,
            state: data.state,
            dueDate: data.dueDate,
            remarks: data.remarks,
            silent: true,
          })
        );
        if (!isThunkFulfilled(statusRes)) {
          if (!data?.silent) {
            notifyApiError(
              statusRes.payload,
              "Failed to update application status"
            );
          }
          return rejectWithValue(
            statusRes.payload ||
              getErrorPayload(null, "Failed to update application status")
          );
        }

        if (!data?.silent) {
          notifyApiSuccess("Your application has been updated!");
        }

        const state = getState();
        const currentUser = state.auth.user?.user;
        if (currentUser && currentUser.company_id) {
          dispatch(
            createNotification({
              message: `Application updated by ${
                currentUser.full_name || currentUser.first_name || "User"
              }`,
              userId: currentUser.id,
              accessToken: data.accessToken,
              is_admin: true,
            })
          );
        }

        return statusRes.payload;
      }

      const payload = {};
      if (hasStatus) payload.state_id = data.state;
      if (data?.json_data !== undefined) payload.json_data = data.json_data;
      if (data?.dueDate) payload.dueDate = data.dueDate;
      if (data?.descripencie != null) payload.descripencie = data.descripencie;
      if (data?.htmlForm) payload.htmlForm = data.htmlForm;
      if (data?.remarks != null && data.remarks !== "") {
        payload.remarks = data.remarks;
      }

      const response = await API.put(
        `/applications/${data?.id}`,
        payload,
        authConfig(data.accessToken, { silent: data?.silent })
      );

      if (!data?.silent) {
        notifyApiSuccess(
          data?.successMessage || "Your application has been updated!"
        );
      }

      // Skip auto company notification for silent / remarks-only maintenance updates
      if (!data?.silent && !data?.skipNotification) {
        const state = getState();
        const currentUser = state.auth.user?.user;
        if (currentUser && currentUser.company_id) {
          dispatch(
            createNotification({
              message: `Application updated by ${
                currentUser.full_name || currentUser.first_name || "User"
              }`,
              userId: currentUser.id,
              accessToken: data.accessToken,
              is_admin: true,
            })
          );
        }
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Failed to update application")
      );
    }
  }
);

export const fetchApplications = createAsyncThunk(
  "fetchApplications",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      let url = "/applications";
      if (data?.queryParams && !_.isEmpty(data?.queryParams)) {
        url = `${url}?${data?.queryParams instanceof Object
          ? qs.stringify(data?.queryParams)
          : data?.queryParams
          }`;
      }
      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      console.error("fetchApplications error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        headers: error.config?.headers
      });
      throw new Error(`Failed to load applications: ${error.response?.data?.message || error.message}`);
    }
  }
);
export const fetchApplicationWithId = createAsyncThunk(
  "fetchApplicationWithId",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.get(`/applications/${data?.id}`, config);
      return response.data;
    } catch (error) {
      throw new Error("Failed to load applications");
    }
  }
);

export const fetchApplicationAuditTrail = createAsyncThunk(
  "fetchApplicationAuditTrail",
  async ({ accessToken, id, page = 1, limit = 20, action }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${accessToken}`,
        },
      };
      const query = { page, limit };
      if (action) query.action = action;
      const response = await API.get(
        `/applications/${id}/audit-trail?${qs.stringify(query)}`,
        config
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load audit trail"
      );
    }
  }
);

const startsWithPdfMagic = (bytes) =>
  bytes?.length >= 4 &&
  bytes[0] === 0x25 && // %
  bytes[1] === 0x50 && // P
  bytes[2] === 0x44 && // D
  bytes[3] === 0x46; // F

const looksLikeCsv = (text) => {
  const firstLine = String(text || "")
    .split(/\r?\n/)
    .find((line) => line.trim()) || "";
  return (
    firstLine.includes(",") &&
    /date|action|description|status|performed/i.test(firstLine)
  );
};

const withExtension = (name, ext) => {
  const base = String(name || "")
    .replace(/\.[^.]+$/, "")
    .trim();
  return `${base || "audit-trail"}.${ext}`;
};

/**
 * Download audit trail export.
 * GET /applications/:id/audit-trail/export
 * API returns CSV (headers like Date, Action, Description, ...).
 */
export const downloadApplicationAuditTrailPdf = async ({
  id,
  accessToken,
  fileName,
}) => {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    Accept: "text/csv, application/pdf, application/octet-stream, */*",
    Authorization: `${accessToken}`,
  };
  const url = `${process.env.REACT_APP_BACKEND_URL}applications/${id}/audit-trail/export`;

  const response = await fetch(url, { method: "GET", headers });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let message = text || `Failed to download audit trail (${response.status})`;
    try {
      const json = JSON.parse(text);
      message = json?.error || json?.message || message;
    } catch {
      /* plain text body */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const asText = new TextDecoder().decode(bytes);

  // Prefer filename from Content-Disposition when provided
  let downloadName = fileName || `audit-trail-GB-${id}`;
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  );
  if (match) {
    downloadName = decodeURIComponent(match[1] || match[2]).trim();
  }

  let blob;
  if (
    contentType.includes("text/csv") ||
    contentType.includes("application/csv") ||
    looksLikeCsv(asText)
  ) {
    // UTF-8 BOM helps Excel open CSV correctly
    const csvWithBom = asText.charCodeAt(0) === 0xfeff ? asText : `\uFEFF${asText}`;
    blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8" });
    downloadName = withExtension(downloadName, "csv");
  } else if (startsWithPdfMagic(bytes) || contentType.includes("application/pdf")) {
    blob = new Blob([bytes], { type: "application/pdf" });
    downloadName = withExtension(downloadName, "pdf");
  } else if (contentType.includes("application/json") || asText.trim().startsWith("{")) {
    let json;
    try {
      json = JSON.parse(asText);
    } catch {
      throw new Error("Server did not return a valid export file");
    }
    throw new Error(
      json?.error || json?.message || "Server did not return a CSV/PDF export"
    );
  } else {
    throw new Error("Server did not return a valid audit trail export");
  }

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = downloadName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};
export const postForm = createAsyncThunk("postForms", async (data) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.accessToken}`,
      },
    };

    const response = await API.post("/forms", { ...data?.data }, config);
    notifyApiSuccess("Your application has been created!");
    return response.data;
  } catch (error) {
    throw new Error("Failed to submit form");
  }
});
export const updateForm = createAsyncThunk("updateForm", async (data, id) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.accessToken}`,
      },
    };
    const response = await API.put(
      `/forms/${data?.id}`,
      { ...data?.data },
      config
    );
    notifyApiSuccess("Your application has been updated!");

    return response.data;
  } catch (error) {
    throw new Error("Failed to submit form");
  }
});
export const verifyApplicationName = createAsyncThunk(
  "verifyApplicationName",
  async (data) => {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.accessToken}`,
      },
    };
    return API.post("/forms/verify-application-name", { ...data }, config);
  }
);

export const addCertificateAndRenewal = (data) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append("issue_date", data.issue_date);
    formData.append("due_date", data.due_date);
    formData.append("user_id", data.user_id);
    if (data.file) formData.append("file", data.file);

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}applications/${data.id}/certificate&renewal`,
      {
        method: "PUT",
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${data.authToken}`,
        },
        body: formData,
      }
    );

    if (!response.ok) throw new Error("Failed to update certificate and renewal");

    const responseData = await response.json();

    dispatch({ type: "CERTIFICATE_UPLOADED", payload: responseData });
  } catch (error) {
    dispatch({ type: "CERTIFICATE_UPLOAD_FAILED", payload: error.message });
  }
};

export const getAllCertificates = async (data) => {
  try {
    // Call the API
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}applications/users/certificates`,
      {
        method: "GET",
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${data.authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to Get Certificates");
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error("Error Getting data:", error);
  }
};

export const fetchV2Applications = async (data) => {
  try {
    const { accessToken, ...queryParams } = data;

    // Filter out undefined or null query parameters
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => value !== undefined && value !== null)
    );

    // Prepare the query parameters string if any valid query parameters are available
    const queryString = new URLSearchParams(filteredParams).toString();
    const url = queryString ? `${process.env.REACT_APP_BACKEND_URL}applications?${queryString}` : `${process.env.REACT_APP_BACKEND_URL}applications`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken, // Add 'Bearer' if required by your backend
      },
    });

    // Check for successful response
    if (!response.ok) {
      throw new Error("Failed to load applications");
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching applications:", error);
    throw new Error("Failed to load applications");
  }
};

const extractTotalFromResponse = (responseData, pageApplications = []) => {
  if (typeof responseData?.total === "number") return responseData.total;
  if (typeof responseData?.pagination?.total === "number")
    return responseData.pagination.total;
  if (typeof responseData?.pagination?.totalItems === "number")
    return responseData.pagination.totalItems;
  return pageApplications.length;
};

const extractApplicationsFromResponse = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.applications)) return responseData.applications;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
};

const extractTotalPages = (responseData, pageApplications, pageSize, currentPage) => {
  const totalPages =
    responseData?.totalPages ||
    responseData?.pagination?.total_pages ||
    responseData?.pagination?.totalPages ||
    null;

  if (totalPages != null) {
    return { hasMore: currentPage < totalPages, totalPages };
  }

  if (pageApplications.length < pageSize) {
    return { hasMore: false, totalPages: currentPage };
  }

  return { hasMore: true, totalPages: null };
};

/**
 * Fetches dashboard application statistics using each status filter's `total`.
 * Matches list-page filters so cards stay in sync with those pages.
 */
export const fetchApplicationCounts = createAsyncThunk(
  "fetchApplicationCounts",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };

      const fetchTotal = async (queryParams) => {
        const url = `/applications?${qs.stringify(queryParams)}`;
        const response = await API.get(url, config);
        const apps = extractApplicationsFromResponse(response.data);
        return extractTotalFromResponse(response.data, apps);
      };

      const [inProcess, pending, discrepancies, approved, renewal] =
        await Promise.all([
          fetchTotal({ page: 1, limit: 1, state: "in_process" }),
          fetchTotal({ page: 1, limit: 1, state: "pending_for_approval" }),
          fetchTotal({ page: 1, limit: 1, state: DISCREPANCY_STATES }),
          fetchTotal({ page: 1, limit: 1, state: "approved" }),
          // Same filter used by Renewals page
          fetchTotal({
            page: 1,
            limit: 1,
            state: "approved",
            dueInDays: 15,
          }),
        ]);

      return {
        in_process: inProcess,
        pending_for_approval: pending,
        discrepancies,
        approved,
        renewal,
      };
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to load application counts"
      );
    }
  }
);

/**
 * Fetches all applications (all pages) to calculate accurate total counts.
 * Used as a fallback when the status-total count requests fail.
 */
export const fetchAllApplicationsForCounts = createAsyncThunk(
  "fetchAllApplicationsForCounts",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };

      let allApplications = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageSize = 100;

      while (hasMorePages) {
        const queryParams = {
          page: currentPage,
          limit: pageSize,
        };

        const response = await API.get(
          `/applications?${qs.stringify(queryParams)}`,
          config
        );
        const responseData = response.data;
        const pageApplications = extractApplicationsFromResponse(responseData);

        allApplications = allApplications.concat(pageApplications);

        const { hasMore } = extractTotalPages(
          responseData,
          pageApplications,
          pageSize,
          currentPage
        );
        hasMorePages = hasMore;
        currentPage += 1;

        // Safety limit
        if (currentPage > 50) {
          hasMorePages = false;
        }
      }

      return allApplications;
    } catch (error) {
      throw new Error(
        `Failed to load all applications for counts: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  }
);

const applicationSlice = createSlice({
  name: "applications",
  initialState,
  reducers: {
    clearApplications: (state) => {
      state.data = [];
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };
      state.fetchError = null;
    },
    clearAuditTrail: (state) => {
      state.auditTrail = {
        logs: [],
        loading: false,
        error: null,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        },
      };
    },
    resetCountsState: (state) => {
      state.counts = {
        newApplications: 0,
        pendingFromDW: 0,
        discrepencies: 0,
        approved: 0,
        renewal: 0,
      };
      state.countsLoading = false;
      state.countsError = null;
      state.fallbackAttempted = false;
      state.allApplications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.fetchLoading = false;
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
          // For array responses, calculate pagination based on length
          const totalPages = Math.ceil(action.payload.length / 10);
          state.pagination = {
            currentPage: 1,
            totalPages: totalPages || 1,
            totalItems: action.payload.length,
            itemsPerPage: 10,
          };
        } else {
          state.data = action.payload.data || action.payload.applications || [];
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.current_page || action.payload.pagination.page || 1,
              totalPages: action.payload.pagination.total_pages || action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.total || action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.per_page || action.payload.pagination.limit || 10,
            };
          } else if (action.payload.currentPage && action.payload.totalPages) {
            // Handle direct pagination metadata in response (your API format)
            state.pagination = {
              currentPage: action.payload.currentPage || 1,
              totalPages: action.payload.totalPages || 1,
              totalItems: action.payload.total || 0,
              itemsPerPage: 10,
            };
          } else {
            // Fallback pagination if no metadata provided
            const totalPages = Math.ceil(state.data.length / 10);
            state.pagination = {
              currentPage: 1,
              totalPages: totalPages || 1,
              totalItems: state.data.length,
              itemsPerPage: 10,
            };
          }
        }
        state.fetchError = null;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error.message;
        state.data = null;
        state.message = null;
      })
      .addCase(fetchApplicationWithId.pending, (state) => {
        state.fetchLoading = true;
        state.currentApp = {};
      })
      .addCase(fetchApplicationWithId.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.currentApp = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchApplicationWithId.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error.message;
        state.currentApp = null;
        state.message = null;
      })
      .addCase(fetchApplicationCounts.pending, (state) => {
        state.countsLoading = true;
        state.countsError = null;
      })
      .addCase(fetchApplicationCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.countsError = null;
        // Map the API response to our expected format
        if (action.payload) {
          state.counts = {
            newApplications: action.payload.in_process || action.payload.newApplications || 0,
            pendingFromDW: action.payload.pending_for_approval || action.payload.pendingFromDW || 0,
            discrepencies: action.payload.discrepancies || action.payload.discrepencies || 0,
            approved: action.payload.approved || 0,
            renewal: action.payload.renewal || 0,
          };
        }
      })
      .addCase(fetchApplicationCounts.rejected, (state, action) => {
        state.countsLoading = false;
        state.countsError = action.error.message;
        // If full application list is already loaded, derive card totals from it
        if (state.allApplications?.length) {
          state.counts = calculateApplicationCounts(state.allApplications);
          state.countsError = null;
        }
      })
      .addCase(fetchAllApplicationsForCounts.pending, (state) => {
        // Used for charts/company panel — do not toggle card spinner
        state.fallbackAttempted = true;
      })
      .addCase(fetchAllApplicationsForCounts.fulfilled, (state, action) => {
        const applicationsArray = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.applications || action.payload?.data || [];

        state.allApplications = applicationsArray;

        // Only overwrite card totals when status-total request failed
        if (state.countsError) {
          state.counts = calculateApplicationCounts(applicationsArray);
          state.countsError = null;
          state.countsLoading = false;
        }
      })
      .addCase(fetchAllApplicationsForCounts.rejected, (state, action) => {
        // Keep existing card totals if status-count already succeeded
        if (state.countsError) {
          state.countsLoading = false;
        }
      })
      .addCase(fetchApplicationAuditTrail.pending, (state) => {
        state.auditTrail.loading = true;
        state.auditTrail.error = null;
      })
      .addCase(fetchApplicationAuditTrail.fulfilled, (state, action) => {
        state.auditTrail.loading = false;
        state.auditTrail.logs = action.payload?.logs || [];
        state.auditTrail.pagination = {
          currentPage: action.payload?.currentPage || 1,
          totalPages: action.payload?.totalPages || 1,
          totalItems: action.payload?.total || 0,
        };
        state.auditTrail.error = null;
      })
      .addCase(fetchApplicationAuditTrail.rejected, (state, action) => {
        state.auditTrail.loading = false;
        state.auditTrail.error = action.error.message;
        state.auditTrail.logs = [];
      });
  },
});

export const { clearApplications, resetCountsState, clearAuditTrail } =
  applicationSlice.actions;
export default applicationSlice.reducer;
